import React, { useMemo, useCallback } from 'react';
import { useTenantDelete, useTenantGetFilters, useTenantList } from '../hooks/use-tenant.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type { Tenant } from '../types/tenant.types';
import SysAdminTenantTable from '../components/sysadmin-tenant-table.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import { message } from 'antd';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { WithSysAdminPermissionCheck } from '../../../../auth/WithSysAdminPermissionCheck';

import { Country } from '../../countries/types/country.types';
import { extractCreateError } from '@services/extract-api-error.service';

const SysAdminTenantTableContainer = () => {
  const { t } = useTranslation();
  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  const { data: availableFilters, isLoading: isAvailableFiltersLoading } = useTenantGetFilters();

  const { data, isLoading, isFetching } = useTenantList(queryParams);
  const { docs: tableData = [] } = data ?? DEFAULT_PAGINATED_TABLE_DATA;

  const deleteTenantMutation = useTenantDelete();

  const handleDeleteTenant = useCallback(async (record: Tenant) => {
    try {
      await deleteTenantMutation.mutateAsync(record.id);
      message.success(t('crud.delete.success', { entity: t('tenants.title') }));
    } catch (error) {
      const errorMessage = extractCreateError(
        error,
        t('crud.delete.error', { entity: t('tenants.title') })
      );
      message.error(errorMessage);
    }
  }, []);

  // Handle table sorting
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        // Convert antd sorter.order to our expected sortOrder format
        let sortOrder: 'asc' | 'desc';

        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          // When clicking the third time (cancel sort), cycle back to ascending
          sortOrder = 'asc';
        }

        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  const columns = useMemo<ColumnConfig<Tenant>[]>(
    () => [
      {
        title: t('tenants.name.label'),
        key: 'name',
        dataIndex: 'name',
        sorter: true,
      },
      {
        title: t('tenants.countryId.label'),
        key: 'country',
        dataIndex: 'country',
        render: (country: Country) => country.code,
      },
      {
        title: t('tenants.region.label'),
        key: 'region',
        dataIndex: 'region',
      },
      {
        title: t('tenants.primaryEmail.label'),
        key: 'primaryEmail',
        dataIndex: 'primaryEmail',
      },
      {
        title: t('tenants.primaryPhone.label'),
        key: 'primaryPhone',
        dataIndex: 'primaryPhone',
      },
      {
        title: t('tenants.parentTenantId.label'),
        key: 'parentTenantId',
        dataIndex: 'parentTenantId',
      },
      {
        title: t('tenants.contractStartDate.label'),
        key: 'contractStartDate',
        dataIndex: 'contractStartDate',
      },
      {
        title: t('tenants.contractEndDate.label'),
        key: 'contractEndDate',
        dataIndex: 'contractEndDate',
      },

      {
        title: t('common.actions'),
        key: 'actions',
        align: 'right',
        render: (_: any, record: Tenant) => (
          <ActionButtons
            record={record}
            baseUrl="/dashboard/admin/tenant"
            onDelete={handleDeleteTenant}
            isDeleting={
              deleteTenantMutation.isPending && deleteTenantMutation.variables === record.id
            }
          />
        ),
      },
    ],
    [handleDeleteTenant, deleteTenantMutation.isPending, deleteTenantMutation.variables]
  );

  return (
    <>
      <HeaderRow
        title={t('crud.list.title', { entity: t('tenants.title') })}
        createRoute="/dashboard/admin/tenant/create"
        showCreateButton={true}
        isSysAdmin={true}
      />
      <div className="row mt-3">
        <SysAdminTenantTable
          data={tableData}
          columns={columns}
          loading={
            isLoading || deleteTenantMutation.isPending || isFetching || isAvailableFiltersLoading
          }
          pagination={data}
          rowKey="id"
          showSizeChanger={true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSearchBar={true}
          onSearchChange={handleSearchChange}
          showFilters={true}
          availableFilters={availableFilters?.data}
          onFilterChange={handleFilterChange}
          tableProps={{
            onChange: handleTableChange,
            sortDirections: ['ascend', 'descend'],
          }}
          sortField={queryParams.sortField}
          sortOrder={queryParams.sortOrder}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(SysAdminTenantTableContainer, 'tenant.view');
